import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import { ArrowLeft, Edit01, CheckCircle } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { WorkflowViewer } from '@/components/application/workflow/workflow-viewer';
import { useWorkflow, useUpdateWorkflow } from '@/hooks/api/use-workflows';
import type { WorkflowNode, WorkflowEdge } from '@/types/workflow';
import { useTranslation } from 'react-i18next';

export const WorkflowDetailPage = () => {
    const { workflowId } = useParams<{ workflowId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation(['pages:workflows-admin', 'common']);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const edgesDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const savedTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Redirect if workflowId is 'new' (should go to edit page)
    useEffect(() => {
        if (workflowId === 'new') {
            navigate('/admin/workflows/new', { replace: true });
        }
    }, [workflowId, navigate]);

    // Use API hooks - only if workflowId is valid and not 'new'
    const shouldLoadWorkflow = workflowId && workflowId !== 'new';
    const { data: workflow, isLoading } = useWorkflow(shouldLoadWorkflow ? workflowId : '');
    const updateWorkflowMutation = useUpdateWorkflow();

    // Don't render if redirecting
    if (workflowId === 'new') {
        return null;
    }

    const handleNodesPositionChange = useCallback((updatedNodes: WorkflowNode[]) => {
        if (!workflow || !workflowId) return;

        // Update status to 'saving'
        setSaveStatus('saving');

        // Clear previous timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Clear saved timer if active
        if (savedTimerRef.current) {
            clearTimeout(savedTimerRef.current);
        }

        // New timer with 1 second debounce
        debounceTimerRef.current = setTimeout(async () => {
            try {
                await updateWorkflowMutation.mutateAsync({
                    workflowId,
                    data: { nodes: updatedNodes },
                });

                setSaveStatus('saved');

                // Reset to 'idle' after 2 seconds
                savedTimerRef.current = setTimeout(() => {
                    setSaveStatus('idle');
                }, 2000);
            } catch (error) {
                console.error('Error updating node positions:', error);
                setSaveStatus('idle');
            }
        }, 1000);
    }, [workflow, workflowId, updateWorkflowMutation]);

    const handleEdgesChange = useCallback((updatedEdges: WorkflowEdge[]) => {
        if (!workflow || !workflowId) return;

        // Update status to 'saving'
        setSaveStatus('saving');

        // Clear previous timer
        if (edgesDebounceTimerRef.current) {
            clearTimeout(edgesDebounceTimerRef.current);
        }

        // Clear saved timer if active
        if (savedTimerRef.current) {
            clearTimeout(savedTimerRef.current);
        }

        // New timer with 1 second debounce
        edgesDebounceTimerRef.current = setTimeout(async () => {
            try {
                await updateWorkflowMutation.mutateAsync({
                    workflowId,
                    data: { edges: updatedEdges },
                });

                setSaveStatus('saved');

                // Reset to 'idle' after 2 seconds
                savedTimerRef.current = setTimeout(() => {
                    setSaveStatus('idle');
                }, 2000);
            } catch (error) {
                console.error('Error updating edges:', error);
                setSaveStatus('idle');
            }
        }, 1000);
    }, [workflow, workflowId, updateWorkflowMutation]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('common:loading')}</p>
                </div>
            </div>
        );
    }

    if (!workflow) {
        return (
            <div className="text-center py-12">
                <h3 className="text-lg font-medium text-primary mb-2">{t('workflows.notFound.title')}</h3>
                <p className="text-tertiary mb-4">{t('workflows.notFound.message')}</p>
                <Button onClick={() => navigate('/admin/workflows', { state: navigationStateFrom(location) })} size="md" color="primary">
                    {t('workflows.notFound.backToList')}
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[calc(100vh-120px)]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-secondary">
                <div className="flex items-center gap-4">
                    <Link to="/admin/workflows" state={navigationStateFrom(location)}>
                        <Button size="sm" color="secondary" iconLeading={ArrowLeft}>
                            {t('workflows.detail.back')}
                        </Button>
                    </Link>
                    <h2 className="text-xl font-semibold text-primary">{workflow.name}</h2>
                </div>
                <Button
                    onClick={() => navigate(`/admin/workflows/${workflow.workflow_id}/edit`, { state: navigationStateFrom(location) })}
                    size="md"
                    color="primary"
                    iconLeading={Edit01}
                >
                    {t('workflows.detail.edit')}
                </Button>
            </div>

            {/* Workflow Viewer - Full Page */}
            <div className="flex-1 overflow-hidden relative">
                {/* Save Status Indicator */}
                {saveStatus !== 'idle' && (
                    <div className="absolute top-4 right-4 z-10 flex items-center gap-2 rounded-lg bg-primary px-3 py-2 shadow-lg ring-1 ring-secondary">
                        {saveStatus === 'saving' ? (
                            <>
                                <div className="size-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                                <span className="text-sm text-primary">{t('workflows.savingChanges')}</span>
                            </>
                        ) : (
                            <>
                                <CheckCircle className="size-4 text-utility-success-600" />
                                <span className="text-sm text-primary">{t('workflows.allSaved')}</span>
                            </>
                        )}
                    </div>
                )}
                <WorkflowViewer
                    nodes={workflow.nodes || []}
                    edges={workflow.edges || []}
                    workflowId={workflow.workflow_id}
                    onNodesPositionChange={handleNodesPositionChange}
                    onEdgesChange={handleEdgesChange}
                />
            </div>
        </div>
    );
};
